import styled from 'styled-components';

export const NavOverlay = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  inset: 0;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 90;
  opacity: ${({ $isOpen }) => ($isOpen ? '1' : '0')};
  visibility: ${({ $isOpen }) => ($isOpen ? 'visible' : 'hidden')};
  transition: opacity 0.3s ease, visibility 0.3s ease;

  @media (min-width: ${({ theme }) => theme.breakpoints.lg}) {
    display: none;
  }
`;

export const NavContainer = styled.nav<{ $isOpen: boolean; $isCollapsed?: boolean }>`
  position: fixed;
  top: 70px;
  left: 0;
  bottom: 0;
  width: ${({ $isCollapsed }: { $isOpen: boolean; $isCollapsed?: boolean }) => $isCollapsed ? '80px' : '280px'};
  background-color: ${({ theme }) => theme.colors.surface};
  box-shadow: ${({ theme }) => theme.shadows.lg};
  z-index: 95;
  transform: translateX(${({ $isOpen }) => ($isOpen ? '0' : '-100%')});
  transition: transform 0.3s ease, width 0.3s ease;

  @media (min-width: ${({ theme }) => theme.breakpoints.lg}) {
    transform: translateX(0);
    border-right: 1px solid ${({ theme }) => theme.colors.borderSubtle};
    border-bottom: none;
    background-color: ${({ theme }) => theme.colors.surface};
    z-index: 90;
  }
`;

export const NavContent = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: ${({ theme }) => theme.spacing[4]};

  @media (min-width: ${({ theme }) => theme.breakpoints.lg}) {
    padding: ${({ theme }) => theme.spacing[6]};
  }
`;

export const NavPrimaryAction = styled.button<{ $isCollapsed?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: ${({ $isCollapsed }) => ($isCollapsed ? 'center' : 'flex-start')};
  gap: ${({ theme }) => theme.spacing[3]};
  width: 100%;
  padding: ${({ theme }) => `${theme.spacing[3]} ${theme.spacing[4]}`};
  margin-bottom: ${({ theme }) => theme.spacing[5]};
  background: ${({ theme }) => theme.colors.primary};
  color: white;
  border: none;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  text-align: left;
  cursor: pointer;
  transition: background 0.2s ease;

  &:hover {
    background: ${({ theme }) => theme.colors.primaryDark};
  }

  svg {
    flex-shrink: 0;
  }
`;

export const NavList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[2]};
`;

export const NavGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[1]};

  & + & {
    margin-top: ${({ theme }) => theme.spacing[4]};
  }
`;

export const NavGroupLabel = styled.button<{ $isCollapsed?: boolean }>`
  display: ${({ $isCollapsed }) => ($isCollapsed ? 'none' : 'flex')};
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: ${({ theme }) => theme.spacing[1]} ${({ theme }) => theme.spacing[4]};
  margin-bottom: ${({ theme }) => theme.spacing[1]};
  background: none;
  border: none;
  cursor: pointer;
  font-size: 10.5px;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.textMuted};

  svg {
    width: 12px;
    height: 12px;
    flex-shrink: 0;
    transition: transform 0.15s ease;
  }
`;

export const NavItem = styled.li`
  width: 100%;
`;

export const NavIconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 19px;
  height: 19px;
  color: ${({ theme }) => theme.colors.primary};
  
  svg {
    width: 100%;
    height: 100%;
  }
`;

export const NavLink = styled.button<{ $isActive: boolean; $isCollapsed?: boolean }>`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[3]};
  width: 100%;
  padding: ${({ theme }) => `${theme.spacing[2]} ${theme.spacing[4]}`};
  justify-content: ${({ $isCollapsed }: { $isActive: boolean; $isCollapsed?: boolean }) => $isCollapsed ? 'center' : 'flex-start'};
  background: ${({ theme, $isActive }) =>
    $isActive ? theme.colors.primary : 'transparent'};
  color: ${({ theme, $isActive }) =>
    $isActive ? 'white' : theme.colors.gray700};
  border: none;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeight.regular};
  text-align: left;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background-color: ${({ theme, $isActive }) =>
    $isActive ? theme.colors.primaryDark : theme.colors.gray100};
    color: ${({ theme, $isActive }) =>
    $isActive ? 'white' : theme.colors.gray900};

    ${NavIconWrapper} {
      color: ${({ theme, $isActive }) =>
    $isActive ? 'white' : theme.colors.gray900};
    }
  }

  ${NavIconWrapper} {
    color: ${({ theme, $isActive }) =>
    $isActive ? 'white' : theme.colors.primary};
  }
`;



export const NavLabel = styled.span<{ $isCollapsed?: boolean }>`
  flex: 1;
  display: ${({ $isCollapsed }) => $isCollapsed ? 'none' : 'block'};
  opacity: ${({ $isCollapsed }) => $isCollapsed ? 0 : 1};
  transition: opacity 0.2s ease;
  white-space: nowrap;
`;

export const CollapseButton = styled.button`
  position: absolute;
  bottom: 24px;
  right: -12px;
  width: 24px;
  height: 24px;
  background-color: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 100;
  color: ${({ theme }) => theme.colors.gray500};
  box-shadow: ${({ theme }) => theme.shadows.sm};
  transition: all 0.2s ease;

  &:hover {
    color: ${({ theme }) => theme.colors.primary};
    border-color: ${({ theme }) => theme.colors.primary};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    display: none;
  }
`;
